import { useState, useEffect } from 'react'
import { Calendar, Plus, TrendingUp, Beef } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { Button, Card } from '@/components/common'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import CowWeightFormModal from './CowWeightFormModal'
import { API_BASE_URL, API_ENDPOINTS } from '@/constants'
import { storageService } from '@/services'

// Cattle types
const CATTLE_TYPES = [
  { value: 'BREEDING_COW', label: 'แม่พันธุ์' },
  { value: 'PREGNANT_COW', label: 'แม่ท้อง' },
  { value: 'BREEDING_BULL', label: 'พ่อพันธุ์' },
  { value: 'FATTENING_CATTLE', label: 'โคขุน' },
  { value: 'CALF', label: 'ลูกโค' },
  { value: 'YOUNG_CATTLE', label: 'โครุ่น' },
]

interface WeightRecord {
  id: string
  cowId: string
  cowName: string
  cowCode: string
  cattleType: string
  date: string
  weight: number
  notes?: string
}

type DateRange = '7' | '14' | '30' | '90' | 'custom'

export default function CowWeightPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedCattleType, setSelectedCattleType] = useState<string>('BREEDING_COW')
  const [showModal, setShowModal] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange>('7')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')

  // Generate chart data based on selected date range - น้ำหนักเฉลี่ยต่อกลุ่มต่อวัน
  const getChartData = () => {
    const daysToShow = dateRange === 'custom' ? 7 : parseInt(dateRange)

    // Sample data for demonstration - น้ำหนักเฉลี่ย (กก.)
    const sampleData = [
      { date: '7 พ.ย.', BREEDING_COW: 450, PREGNANT_COW: 480, BREEDING_BULL: 520, FATTENING_CATTLE: 380, CALF: 120, YOUNG_CATTLE: 250 },
      { date: '8 พ.ย.', BREEDING_COW: 452, PREGNANT_COW: 482, BREEDING_BULL: 522, FATTENING_CATTLE: 382, CALF: 122, YOUNG_CATTLE: 252 },
      { date: '9 พ.ย.', BREEDING_COW: 451, PREGNANT_COW: 481, BREEDING_BULL: 521, FATTENING_CATTLE: 381, CALF: 121, YOUNG_CATTLE: 251 },
      { date: '10 พ.ย.', BREEDING_COW: 453, PREGNANT_COW: 483, BREEDING_BULL: 523, FATTENING_CATTLE: 383, CALF: 123, YOUNG_CATTLE: 253 },
      { date: '11 พ.ย.', BREEDING_COW: 454, PREGNANT_COW: 484, BREEDING_BULL: 524, FATTENING_CATTLE: 384, CALF: 124, YOUNG_CATTLE: 254 },
      { date: '12 พ.ย.', BREEDING_COW: 455, PREGNANT_COW: 485, BREEDING_BULL: 525, FATTENING_CATTLE: 385, CALF: 125, YOUNG_CATTLE: 255 },
      { date: '13 พ.ย.', BREEDING_COW: 456, PREGNANT_COW: 486, BREEDING_BULL: 526, FATTENING_CATTLE: 386, CALF: 126, YOUNG_CATTLE: 256 },
    ]

    return sampleData.slice(Math.max(0, sampleData.length - daysToShow))
  }

  const chartData = getChartData()

  // Weight records state - เก็บข้อมูลการบันทึกน้ำหนัก
  const [allWeightRecords, setAllWeightRecords] = useState<WeightRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Fetch weight records
  useEffect(() => {
    fetchWeightRecords()
  }, [selectedDate, selectedCattleType])

  const fetchWeightRecords = async () => {
    try {
      setIsLoading(true)
      const token = storageService.getAuthToken()
      const dateStr = selectedDate.toISOString().split('T')[0]

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.HEALTH.WEIGHT_RECORDS}?date=${dateStr}&cattleType=${selectedCattleType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        // Map response to match our interface
        const mappedRecords = data.map((record: any) => ({
          id: record.id,
          cowId: record.cowId,
          cowName: record.cow.name || '',
          cowCode: record.cow.cowCode,
          cattleType: record.cow.cattleType,
          date: record.date.split('T')[0],
          weight: record.weight,
          notes: record.notes || '',
        }))
        setAllWeightRecords(mappedRecords)
      }
    } catch (error) {
      console.error('Error fetching weight records:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Filter records by selected date and cattle type
  const dateStr = selectedDate.toISOString().split('T')[0]
  const weightRecords = allWeightRecords.filter(
    (record) => record.date === dateStr && record.cattleType === selectedCattleType
  )

  const handleAddNew = () => {
    setShowModal(true)
  }

  const handleSave = async (records: WeightRecord[], formDate: Date) => {
    try {
      const token = storageService.getAuthToken()
      const dateStr = formDate.toISOString().split('T')[0]

      // Prepare data for bulk create
      const recordsToSave = records.map((record) => ({
        cowId: record.cowId,
        date: dateStr,
        weight: record.weight,
        notes: record.notes || '',
      }))

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.HEALTH.BULK_CREATE_WEIGHTS}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ records: recordsToSave }),
        }
      )

      if (response.ok) {
        // Refresh data
        await fetchWeightRecords()
        setShowModal(false)
        alert('บันทึกน้ำหนักสำเร็จ')
      } else {
        const error = await response.json()
        alert(`เกิดข้อผิดพลาด: ${error.message || 'ไม่สามารถบันทึกข้อมูลได้'}`)
      }
    } catch (error) {
      console.error('Error saving weight records:', error)
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
    }
  }

  const getCattleTypeLabel = (type: string) => {
    return CATTLE_TYPES.find((t) => t.value === type)?.label || type
  }

  // Calculate stats for selected date and cattle type
  const recordsForDate = allWeightRecords.filter(
    (record) => record.date === dateStr && record.cattleType === selectedCattleType
  )
  const totalCows = recordsForDate.length
  const averageWeight =
    totalCows > 0 ? recordsForDate.reduce((sum, r) => sum + r.weight, 0) / totalCows : 0
  const maxWeight = totalCows > 0 ? Math.max(...recordsForDate.map((r) => r.weight)) : 0
  const minWeight = totalCows > 0 ? Math.min(...recordsForDate.map((r) => r.weight)) : 0

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
              การบันทึกน้ำหนักโค
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              บันทึกและติดตามน้ำหนักแต่ละตัวในแต่ละกลุ่มวัว
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleAddNew}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>บันทึกน้ำหนัก</span>
          </Button>
        </div>

        {/* Graph Card */}
        <Card>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-800" />
              <h2 className="text-lg font-semibold text-text-primary">
                กราฟน้ำหนักเฉลี่ยต่อกลุ่มต่อวัน
              </h2>
            </div>

            {/* Date Range Selector */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setDateRange('7')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '7'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                7 วัน
              </button>
              <button
                onClick={() => setDateRange('14')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '14'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                14 วัน
              </button>
              <button
                onClick={() => setDateRange('30')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '30'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                1 เดือน
              </button>
              <button
                onClick={() => setDateRange('90')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '90'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                3 เดือน
              </button>
              <button
                onClick={() => setDateRange('custom')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                กำหนดเอง
              </button>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {dateRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
              <label className="text-sm font-medium text-text-primary">ช่วงเวลา:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-1.5 border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800"
                placeholder="วันเริ่มต้น"
              />
              <span className="text-text-secondary">ถึง</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-1.5 border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800"
                placeholder="วันสิ้นสุด"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  console.log('Custom range:', customStartDate, 'to', customEndDate)
                }}
              >
                แสดงข้อมูล
              </Button>
            </div>
          )}

          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'น้ำหนัก (กก.)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="BREEDING_COW"
                  stroke="#ec4899"
                  name="แม่พันธุ์"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="PREGNANT_COW"
                  stroke="#f97316"
                  name="แม่ท้อง"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="BREEDING_BULL"
                  stroke="#3b82f6"
                  name="พ่อพันธุ์"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="FATTENING_CATTLE"
                  stroke="#10b981"
                  name="โคขุน"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="CALF"
                  stroke="#a855f7"
                  name="ลูกโค"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="YOUNG_CATTLE"
                  stroke="#f59e0b"
                  name="โครุ่น"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px]">
              <p className="text-text-tertiary">ไม่มีข้อมูลสำหรับแสดงกราฟ</p>
            </div>
          )}
        </Card>

        {/* Calendar & Records */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Calendar Sidebar */}
          <div className="lg:col-span-3">
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-primary-800" />
                <h3 className="font-semibold text-text-primary">เลือกวันที่</h3>
              </div>
              <div className="space-y-2">
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  className="w-full px-3 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
                />
                <div className="text-sm text-text-secondary mt-4">
                  <p className="font-medium mb-2">วันที่เลือก:</p>
                  <p className="text-lg font-semibold text-primary-800">
                    {selectedDate.toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </Card>

            {/* Summary Stats */}
            <Card className="mt-4">
              <div className="flex items-center gap-2 mb-4">
                <Beef className="w-5 h-5 text-primary-800" />
                <h3 className="font-semibold text-text-primary">
                  สรุป {getCattleTypeLabel(selectedCattleType)}
                </h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-text-tertiary">จำนวนโค</p>
                  <p className="text-xl font-bold text-text-primary">{totalCows} ตัว</p>
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">น้ำหนักเฉลี่ย</p>
                  <p className="text-xl font-bold text-green-600">
                    {averageWeight.toFixed(1)} กก.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-text-tertiary">น้ำหนักสูงสุด</p>
                    <p className="text-lg font-bold text-blue-600">{maxWeight} กก.</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary">น้ำหนักต่ำสุด</p>
                    <p className="text-lg font-bold text-amber-600">{minWeight} กก.</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Records Table with Tabs */}
          <div className="lg:col-span-9">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-text-primary">
                  รายการน้ำหนักโค
                </h2>
                <span className="text-sm text-text-tertiary">
                  {selectedDate.toLocaleDateString('th-TH')}
                </span>
              </div>

              {/* Cattle Type Tabs */}
              <div className="flex gap-2 border-b border-border-light mb-6 overflow-x-auto">
                {CATTLE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedCattleType(type.value)}
                    className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                      selectedCattleType === type.value
                        ? 'text-primary-800 border-b-2 border-primary-800'
                        : 'text-text-tertiary hover:text-text-secondary'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
                </div>
              ) : weightRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-light">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">
                          รหัสโค
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">
                          ชื่อโค
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          น้ำหนัก (กก.)
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">
                          หมายเหตุ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weightRecords.map((record) => (
                        <tr
                          key={record.id}
                          className="border-b border-border-light hover:bg-background-secondary transition-colors"
                        >
                          <td className="py-4 px-4">
                            <p className="text-sm font-medium text-text-primary">
                              {record.cowCode}
                            </p>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm text-text-primary">{record.cowName}</p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-lg font-bold text-green-600">{record.weight}</p>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm text-text-tertiary">
                              {record.notes || '-'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-background-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Beef className="w-8 h-8 text-text-tertiary" />
                  </div>
                  <p className="text-text-secondary mb-2">
                    ยังไม่มีข้อมูลสำหรับ {getCattleTypeLabel(selectedCattleType)}
                  </p>
                  <p className="text-sm text-text-tertiary mb-4">
                    เริ่มบันทึกน้ำหนักสำหรับกลุ่มนี้
                  </p>
                  <Button variant="primary" onClick={handleAddNew}>
                    <Plus className="w-5 h-5 mr-2" />
                    บันทึกน้ำหนัก
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Weight Form Modal */}
      {showModal && (
        <CowWeightFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          selectedDate={selectedDate}
          defaultCattleType={selectedCattleType}
        />
      )}
    </MainLayout>
  )
}
